import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarBlank } from "@phosphor-icons/react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

/**
 * Drop-in replacement for <Input type="date">.
 * - value:  ISO date string "YYYY-MM-DD" (or "")
 * - onChange: receives an event-like { target: { value: "YYYY-MM-DD" } } so existing handlers keep working
 */
export default function DateField({
    value,
    onChange,
    id,
    className = "",
    placeholder = "Pick a date",
    "data-testid": testId,
    disabled = false,
}) {
    const [open, setOpen] = useState(false);

    const parsed =
        value && typeof value === "string"
            ? parse(value, "yyyy-MM-dd", new Date())
            : null;
    const valid = parsed && isValid(parsed) ? parsed : null;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    id={id}
                    data-testid={testId}
                    disabled={disabled}
                    className={`mt-1.5 h-11 w-full justify-start font-normal ${
                        valid ? "text-slate-900" : "text-slate-400"
                    } ${className}`}
                >
                    <CalendarBlank size={16} className="mr-2 shrink-0" />
                    {valid ? format(valid, "MM/dd/yyyy") : placeholder}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={valid || undefined}
                    defaultMonth={valid || undefined}
                    captionLayout="dropdown-buttons"
                    fromYear={1990}
                    toYear={new Date().getFullYear() + 5}
                    classNames={{
                        caption: "flex justify-center pt-1 pb-2 relative items-center gap-2",
                        caption_label: "hidden",
                        caption_dropdowns: "flex items-center gap-2",
                        dropdown:
                            "bg-white border border-slate-200 rounded-md px-2 py-1 text-sm font-medium hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F1E3D]/20 cursor-pointer appearance-none pr-6",
                        dropdown_month: "min-w-[6.5rem]",
                        dropdown_year: "min-w-[5rem]",
                        vhidden: "hidden",
                    }}
                    onSelect={(d) => {
                        const iso = d ? format(d, "yyyy-MM-dd") : "";
                        onChange?.({ target: { value: iso } });
                        setOpen(false);
                    }}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
}
